import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { browserHistory } from 'common/utils/browserHistory';
import removeSelectedContact from 'common/display/ContactStepper/actions/RemoveSelectedContact';
import enforcedConsentRoute from 'common/utils/Navigation/enforcedConsentRoute';
import { DOMAIN_CONSTANTS } from 'src/common/constants';
import { isHttpError } from 'common/utils/Error';
import { getEnumsFromState } from 'common/utils/Enums';
import findCurrentGroup from 'common/utils/findCurrentGroup';
import { getGroup } from 'common/utils/stateHelpers';
import { fetchGroup } from 'actions/Group';
import _ from 'lodash';
import { Button } from '@unite-us/app-components';
import { OverlaySpinner, Spinner } from 'common/spinners';
import { formatNewContactDataFromValues } from 'common/display/ContactStepper/utils';
import {
  createUpdateDeletePaymentsInsurances,
  validateInsuranceForParentForm,
} from 'src/components/Insurance/utils/';
import { isFocused, INSURANCE } from 'common/utils/isFocused';
import createInsuranceEntry from 'src/components/Insurance/actions/createInsuranceEntry';
import '../stylesheets/addContactForm.scss';
import Header from './Header';
import createContact from '../actions/CreateContact';
import retrieveSearchedContacts from '../actions/RetrieveSelectedContact';
import BasicInformation from './BasicInformation';
import SupplementalInformation from './SupplementalInformation';
import ContactInformation from './ContactInformation';

const NOT_FOUND_HEADER_COPY = 'We did not find your client in our records.';
const REGULAR_HEADER_COPY = 'Please fill out your client\'s basic contact information below.';

export class AddContactInformationFromNav extends Component {
  static restartContactForm() {
    browserHistory.push('/contacts/new/search');
  }

  constructor(props) {
    super(props);

    this.onCreateContact = this.onCreateContact.bind(this);
  }

  componentDidMount() {
    if (!this.props.completeGroup) {
      this.props.fetchGroup(this.props.groupId);
    }
  }

  onCreateContact(values) {
    const { groupId } = this.props;

    return this.props.createContact({
      contact: formatNewContactDataFromValues(values),
      groupId,
      showNotification: false,
    }).then((payload) => {
      if (!isHttpError(payload)) {
        const contact = _.wget(payload, 'data.data', {});
        const { id: contactId } = contact;

        createUpdateDeletePaymentsInsurances({
          values,
          contactId,
          groupId,
          createInsuranceEntry: this.props.createInsuranceEntry,
          showNotification: false,
        });

        enforcedConsentRoute({
          contact,
          itemType: DOMAIN_CONSTANTS.CONTACT,
          to: `/facesheet/${contact.id}`,
        });
        this.props.removeSelectedContact();
      }
    });
  }

  renderSupplementalSection(submitFailed = false) {
    const {
      completeGroup,
      currentGroup,
    } = this.props;

    if (!completeGroup) {
      return <Spinner />;
    }

    return (
      <SupplementalInformation
        currentGroup={currentGroup}
        submitFailed={submitFailed}
      />
    );
  }

  render() {
    const {
      enums,
      header,
      initialValues,
      location,
      mainHeader,
    } = this.props;
    const { query: { not_found } } = location;
    const subHeader = not_found ? `${NOT_FOUND_HEADER_COPY} ${REGULAR_HEADER_COPY}` : REGULAR_HEADER_COPY;

    return (
      <Form
        onSubmit={this.onCreateContact}
        initialValues={initialValues}
        mutators={{ ...arrayMutators }}
        validate={(values) => {
          const shouldValidateInsurance = isFocused(
            this.props.currentGroup,
            INSURANCE,
          );
          return validateInsuranceForParentForm(values, shouldValidateInsurance);
        }}
      >
        {({ handleSubmit, submitting, submitFailed }) => (
          <form
            onSubmit={handleSubmit}
            className="add-contact-information"
          >
            <OverlaySpinner text="Creating Client..." show={submitting} />
            <div className="row">
              <div className="referral-header col-md-12">
                <Header
                  header={header}
                  mainHeader={mainHeader}
                  subHeader={subHeader}
                />
              </div>
            </div>

            <BasicInformation enums={enums} />

            <ContactInformation enums={enums} />

            {this.renderSupplementalSection(submitFailed)}

            <footer className="referral-footer">
              <Button
                id="go-back-btn"
                onClick={AddContactInformationFromNav.restartContactForm}
                label="Go Back"
                style={{ marginRight: '10px' }}
              />
              <Button
                id="save-client-btn"
                onClick={handleSubmit}
                label="Save Client"
                primary
                disabled={submitting}
              />
            </footer>
          </form>
        )}
      </Form>
    );
  }
}

AddContactInformationFromNav.propTypes = {
  completeGroup: PropTypes.object,
  createContact: PropTypes.func.isRequired,
  createInsuranceEntry: PropTypes.func.isRequired,
  currentGroup: PropTypes.object.isRequired,
  enums: PropTypes.object,
  fetchGroup: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  header: PropTypes.string.isRequired,
  initialValues: PropTypes.object,
  location: PropTypes.object,
  mainHeader: PropTypes.string,
  removeSelectedContact: PropTypes.func.isRequired,
};

AddContactInformationFromNav.defaultProps = {
  completeGroup: null,
  enums: null,
  initialValues: null,
  location: null,
  mainHeader: null,
};

function mapStateToProps(state) {
  const {
    contacts,
    searchedContacts,
    selectedContact,
    user,
    session,
    session: { groupId },
  } = state;
  const allContacts = [...contacts.contacts, ...searchedContacts];
  const contact = allContacts.find((person) => person.id === selectedContact);

  const initialValues = {
    first_name: _.get(contact, 'first_name', ''),
    last_name: _.get(contact, 'last_name', ''),
    date_of_birth: _.get(contact, 'date_of_birth', ''),
    gender: 'undisclosed',
    marital_status: 'undisclosed',
    race: 'undisclosed',
    ethnicity: 'undisclosed',
    addresses: [undefined],
    insurance: [{}],
    military: _.get(contact, 'military'),
  };

  return {
    completeGroup: getGroup(state, groupId),
    currentGroup: findCurrentGroup(user, session),
    enums: getEnumsFromState(state),
    groupId,
    initialValues,
  };
}

export default connect(
  mapStateToProps,
  {
    createContact,
    createInsuranceEntry,
    fetchGroup,
    removeSelectedContact,
    retrieveSearchedContacts,
  },
)(AddContactInformationFromNav);
